import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, FileText, Shield, Target, Award } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription } from '../components/ui/card';

const Gutachten = () => {
  const expertiseAreas = [
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Risikobewertung',
      description: 'Klare Einordnung von Bauschäden und deren Auswirkungen',
    },
    {
      icon: <Target className="w-8 h-8" />,
      title: 'Handlungsempfehlungen',
      description: 'Konkrete Maßnahmen statt nur Beschreibungen',
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: 'Fundierte Expertise',
      description: 'Über 40 Jahre Erfahrung in der Immobilienbranche',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="flex flex-col">
              <div className="space-y-6">
              <div className="inline-flex items-center space-x-2 text-brand-600 font-medium">
                <FileText className="w-5 h-5" />
                <span>Gutachten</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-brand-900 leading-tight">
                Wenn Analyse zur Entscheidung wird
              </h1>
              <p className="text-xl text-brand-600 leading-relaxed">
                Wir zeigen, was zu tun ist. Sie erhalten eine klare Einordnung der Situation 
                und Bewertung von Risiken.
              </p>
              </div>
              <div className="mt-10">
              <Link to="/kontakt">
                <Button 
                  size="lg" 
                  className="bg-brand-900 hover:bg-brand-800 text-white px-8 py-3 text-base transition-all duration-300 hover:shadow-lg"
                >
                  Gutachten anfragen
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              </div>
            </div>
            <div className="relative h-[400px] lg:h-[500px] rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://images.pexels.com/photos/285437/pexels-photo-285437.jpeg"
                alt="Bausubstanz Detail"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Expertise Areas */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-brand-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-brand-900 mb-4">
              Mehr als nur Beschreibung
            </h2>
            <p className="text-xl text-brand-600 max-w-3xl mx-auto">
              Unsere Gutachten liefern Ihnen die Grundlage für fundierte Entscheidungen
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {expertiseAreas.map((area, index) => (
              <Card key={index} className="border-2 border-brand-200 hover:border-brand-400 transition-all duration-300 hover:shadow-lg">
                <CardHeader className="p-8 text-center">
                  <div className="text-brand-700 mb-4 flex justify-center">
                    {area.icon}
                  </div>
                  <CardTitle className="text-2xl mb-3">{area.title}</CardTitle>
                  <CardDescription className="text-base text-brand-600">
                    {area.description}
                  </CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative h-[600px] rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1632324875014-b5713099bef4"
                alt="Baudetail Analyse"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <h2 className="text-4xl font-bold text-brand-900 mb-6">
                Von der Analyse zur Handlung
              </h2>
              <p className="text-lg text-brand-600 leading-relaxed mb-6">
                Ein Gutachten muss mehr leisten als nur den Ist-Zustand zu beschreiben. 
                Es muss Risiken einordnen, Handlungsoptionen aufzeigen und die Grundlage 
                für Ihre Entscheidung bilden.
              </p>
              <p className="text-lg text-brand-600 leading-relaxed mb-8">
                Unsere Gutachten verbinden technisches Verständnis mit wirtschaftlicher 
                Bewertung. Sie erhalten keine Prosa, sondern klare Aussagen zu:
              </p>
              <div className="space-y-4">
                <div className="p-4 bg-brand-50 rounded-lg">
                  <h3 className="font-bold text-brand-900 mb-2">Zustandsbewertung</h3>
                  <p className="text-brand-600">
                    Technische Analyse der Bausubstanz und Identifikation von Schäden
                  </p>
                </div>
                <div className="p-4 bg-brand-50 rounded-lg">
                  <h3 className="font-bold text-brand-900 mb-2">Risikobewertung</h3>
                  <p className="text-brand-600">
                    Einschätzung der Auswirkungen und möglicher Folgeschäden
                  </p>
                </div>
                <div className="p-4 bg-brand-50 rounded-lg">
                  <h3 className="font-bold text-brand-900 mb-2">Handlungsempfehlung</h3>
                  <p className="text-brand-600">
                    Konkrete Maßnahmen mit Kosteneinschätzung und Priorisierung
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Application Areas */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-brand-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-brand-900 mb-12 text-center">
            Einsatzbereiche
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {['Kaufberatung', 'Schadensbewertung', 'Beweissicherung', 'Sanierungsplanung'].map((area, index) => (
              <div key={index} className="p-6 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 text-center">
                <h3 className="font-bold text-brand-900 text-lg">{area}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center flex flex-col items-center gap-8">
          <h2 className="text-3xl md:text-4xl font-bold text-brand-900">
            Klarheit schaffen. Entscheidungen ermöglichen.
          </h2>
          <p className="text-xl text-brand-600 max-w-3xl">
            Lassen Sie uns gemeinsam Ihre Immobilie analysieren und die richtigen 
            Schritte definieren.
          </p>
          <Link to="/kontakt">
            <Button 
              size="lg" 
              className="bg-brand-900 hover:bg-brand-800 text-white px-10 py-3 text-lg transition-all duration-300 hover:shadow-lg"
            >
              Jetzt Gutachten beauftragen
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Gutachten;
